import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  listTeamUsers,
  createTeamUser,
  updateTeamUser,
  deleteTeamUser,
  type TeamUser,
  type CreateTeamUserInput,
} from '../api/client';
import { PHONE_COUNTRY_OPTIONS, regionForDialCode } from '../constants/phoneCountryCodes';
import {
  combineE164Phone,
  detectBrowserRegion,
  getDefaultPhoneDialCode,
  getRegionFlag,
  splitE164Phone,
} from '../utils/phoneNumber';
import { UserCircle, Plus, Trash2, Star } from 'lucide-react';
import { useError } from '../context/ErrorContext';
import ConfirmDialog from './ConfirmDialog';
import { SkeletonTableRow } from './Skeleton';

const emptyForm = (): CreateTeamUserInput => ({
  label: '',
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  demoAccountRequired: false,
  demoAccountUsername: '',
  demoAccountPassword: '',
  isDefault: false,
});

const TeamUsers: React.FC = () => {
  const { t, i18n } = useTranslation();
  const defaultDialCode = useMemo(
    () => getDefaultPhoneDialCode(i18n.language),
    [i18n.language]
  );
  const [users, setUsers] = useState<TeamUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<CreateTeamUserInput>(emptyForm());
  const [phoneDialCode, setPhoneDialCode] = useState(defaultDialCode);
  const [phoneLocal, setPhoneLocal] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);
  const { showError: showErrorPopup } = useError();

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await listTeamUsers();
      setUsers(data);
    } catch (e) {
      const msg = e instanceof Error ? e.message : t('common.loadFailed');
      setError(msg);
      showErrorPopup(msg);
    } finally {
      setLoading(false);
    }
  }, [showErrorPopup, t]);

  useEffect(() => {
    load();
  }, [load]);

  const openCreate = () => {
    setEditingId(null);
    setForm(emptyForm());
    setPhoneDialCode(defaultDialCode);
    setPhoneLocal('');
    setModalOpen(true);
  };

  const openEdit = (tu: TeamUser) => {
    const fallback = getDefaultPhoneDialCode(i18n.language);
    const { dialCode, localNumber } = splitE164Phone(tu.phone, fallback);
    setEditingId(tu.id);
    setForm({
      label: tu.label,
      firstName: tu.firstName,
      lastName: tu.lastName,
      email: tu.email,
      phone: tu.phone,
      demoAccountRequired: tu.demoAccountRequired,
      demoAccountUsername: tu.demoAccountUsername ?? '',
      demoAccountPassword: tu.demoAccountPassword ?? '',
      isDefault: tu.isDefault,
    });
    setPhoneDialCode(dialCode);
    setPhoneLocal(localNumber);
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setEditingId(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const phone = combineE164Phone(phoneDialCode, phoneLocal);
    if (!phone) {
      showErrorPopup(t('panel.teamUserPhoneRequired'));
      return;
    }
    const payload = { ...form, phone };
    setSubmitting(true);
    try {
      if (editingId) {
        await updateTeamUser(editingId, payload);
      } else {
        await createTeamUser(payload);
      }
      closeModal();
      await load();
    } catch (err) {
      showErrorPopup(err);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = (id: string) => {
    setDeleteConfirmId(id);
  };

  const confirmDelete = async () => {
    if (!deleteConfirmId) return;
    setDeleting(true);
    try {
      await deleteTeamUser(deleteConfirmId);
      setDeleteConfirmId(null);
      await load();
    } catch (err) {
      showErrorPopup(err);
    } finally {
      setDeleting(false);
    }
  };

  const handleSetDefault = async (tu: TeamUser) => {
    if (tu.isDefault) return;
    try {
      await updateTeamUser(tu.id, { isDefault: true });
      await load();
    } catch (err) {
      showErrorPopup(err);
    }
  };

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
            <UserCircle className="h-7 w-7 text-primary-600" />
            {t('panel.teamUsersTitle')}
          </h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            {t('panel.teamUsersSubtitle')}
          </p>
        </div>
        <button
          type="button"
          onClick={openCreate}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-lg font-medium text-white bg-primary-600 hover:bg-primary-700"
        >
          <Plus className="h-4 w-4" />
          {t('panel.teamUserNew')}
        </button>
      </div>

      {error && (
        <p className="mb-4 text-sm text-red-600 dark:text-red-400">{error}</p>
      )}

      <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
          <thead className="bg-gray-50 dark:bg-gray-900/50">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('panel.teamUserLabel')}</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('panel.teamUserName')}</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('panel.teamUserEmail')}</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('panel.teamUserPhone')}</th>
              <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">{t('common.actions')}</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
            {loading ? (
              [...Array(3)].map((_, i) => <SkeletonTableRow key={i} cols={5} />)
            ) : users.length === 0 ? (
              <tr>
                <td colSpan={5} className="px-4 py-8 text-center text-sm text-gray-500 dark:text-gray-400">
                  {t('panel.noTeamUsers')}{' '}
                  <button type="button" onClick={openCreate} className="text-primary-600 hover:underline">
                    {t('panel.teamUserNew')}
                  </button>
                </td>
              </tr>
            ) : (
              users.map(tu => (
                <tr key={tu.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/30">
                  <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">
                    <span className="inline-flex items-center gap-1.5">
                      {tu.label}
                      {tu.isDefault && (
                        <span className="inline-flex items-center gap-0.5 text-xs text-amber-600 dark:text-amber-400" title={t('panel.teamUserDefault')}>
                          <Star className="h-3.5 w-3.5 fill-current" />
                          {t('panel.teamUserDefault')}
                        </span>
                      )}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300">
                    {tu.firstName} {tu.lastName}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300">{tu.email}</td>
                  <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300">{tu.phone}</td>
                  <td className="px-4 py-3 text-right space-x-2">
                    {!tu.isDefault && (
                      <button
                        type="button"
                        onClick={() => handleSetDefault(tu)}
                        className="text-xs text-primary-600 hover:underline"
                      >
                        {t('panel.teamUserSetDefault')}
                      </button>
                    )}
                    <button type="button" onClick={() => openEdit(tu)} className="text-xs text-gray-600 dark:text-gray-400 hover:underline">
                      {t('common.edit')}
                    </button>
                    <button type="button" onClick={() => handleDelete(tu.id)} className="text-red-600 hover:text-red-700 inline-flex">
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {modalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
          <div className="w-full max-w-lg rounded-xl bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 shadow-xl max-h-[90vh] overflow-y-auto">
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                {editingId ? t('panel.teamUserEdit') : t('panel.teamUserNew')}
              </h2>
              <div>
                <label className="block text-sm font-medium mb-1">{t('panel.teamUserLabel')}</label>
                <input
                  required
                  value={form.label}
                  onChange={e => setForm(f => ({ ...f, label: e.target.value }))}
                  className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700"
                  placeholder={t('panel.teamUserLabelPlaceholder')}
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium mb-1">{t('panel.teamUserFirstName')}</label>
                  <input required value={form.firstName} onChange={e => setForm(f => ({ ...f, firstName: e.target.value }))} className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700" />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">{t('panel.teamUserLastName')}</label>
                  <input required value={form.lastName} onChange={e => setForm(f => ({ ...f, lastName: e.target.value }))} className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">{t('panel.teamUserEmail')}</label>
                <input required type="email" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700" />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">{t('panel.teamUserPhone')}</label>
                <div className="flex gap-2">
                  <div className="flex shrink-0 items-center rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 overflow-hidden">
                    <span className="pl-2 text-sm leading-none select-none" aria-hidden>
                      {getRegionFlag(regionForDialCode(phoneDialCode))}
                    </span>
                    <select
                      value={phoneDialCode}
                      onChange={e => setPhoneDialCode(e.target.value)}
                      className="w-[3.75rem] py-2 pl-1 pr-1 text-xs bg-transparent border-0 focus:ring-0 focus:outline-none text-gray-900 dark:text-white"
                      aria-label={t('panel.teamUserPhoneCountry')}
                    >
                      {PHONE_COUNTRY_OPTIONS.map(opt => (
                        <option key={opt.region} value={opt.dialCode}>
                          {opt.dialCode}
                        </option>
                      ))}
                    </select>
                  </div>
                  <input
                    required
                    type="tel"
                    inputMode="tel"
                    autoComplete="tel-national"
                    value={phoneLocal}
                    onChange={e => setPhoneLocal(e.target.value.replace(/[^\d\s()-]/g, ''))}
                    placeholder={t('panel.teamUserPhoneLocalPlaceholder')}
                    className="flex-1 min-w-0 px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700"
                  />
                </div>
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  {t('panel.teamUserPhoneHint', {
                    country: PHONE_COUNTRY_OPTIONS.find(c => c.region === detectBrowserRegion(i18n.language))?.label
                      ?? regionForDialCode(defaultDialCode),
                  })}
                </p>
              </div>
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={form.demoAccountRequired ?? false}
                  onChange={e => setForm(f => ({ ...f, demoAccountRequired: e.target.checked }))}
                />
                {t('panel.teamUserDemoRequired')}
              </label>
              {form.demoAccountRequired && (
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium mb-1">{t('panel.teamUserDemoUser')}</label>
                    <input value={form.demoAccountUsername ?? ''} onChange={e => setForm(f => ({ ...f, demoAccountUsername: e.target.value }))} className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">{t('panel.teamUserDemoPass')}</label>
                    <input value={form.demoAccountPassword ?? ''} onChange={e => setForm(f => ({ ...f, demoAccountPassword: e.target.value }))} className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700" />
                  </div>
                </div>
              )}
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={form.isDefault ?? false} onChange={e => setForm(f => ({ ...f, isDefault: e.target.checked }))} />
                {t('panel.teamUserDefault')}
              </label>
              <div className="flex justify-end gap-2 pt-2">
                <button type="button" onClick={closeModal} className="px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600">
                  {t('common.cancel')}
                </button>
                <button type="submit" disabled={submitting} className="px-4 py-2 rounded-lg bg-primary-600 text-white disabled:opacity-50">
                  {submitting ? t('common.saving') : t('common.save')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <ConfirmDialog
        open={Boolean(deleteConfirmId)}
        onClose={() => {
          if (!deleting) setDeleteConfirmId(null);
        }}
        title={t('panel.teamUserDeleteTitle')}
        message={t('panel.teamUserDeleteConfirm')}
        confirmLabel={deleting ? t('panel.deleting') : t('common.delete')}
        cancelLabel={t('common.cancel')}
        onConfirm={() => void confirmDelete()}
        confirmLoading={deleting}
        closeDisabled={deleting}
        variant="danger"
      />
    </div>
  );
};

export default TeamUsers;
